/**
 * Hook to consume Grida editor handoff token
 * 
 * This simulates what Grida app would do when receiving the handoff token
 */

import { useQuery } from "@tanstack/react-query";
import { logger } from "@/lib/utils/logger";

interface ConsumeTokenResponse {
  ok?: boolean;
  page_config_name?: string;
  user?: {
    email: string;
    full_name?: string;
  };
  redirect?: string;
  error?: string;
}

/**
 * Hook to consume handoff token (simulates Grida app behavior)
 * 
 * Note: In the real Grida app, this would be a POST request to Frappe API.
 * For testing, we'll call it from zvgnext to verify the flow works.
 */
export function useGridaTokenConsume(token: string | null) {
  return useQuery<ConsumeTokenResponse, Error>({
    queryKey: ["grida", "consume-token", token],
    queryFn: async () => {
      if (!token) {
        throw new Error("Token is required");
      }

      try {
        // In real Grida app, this would be a POST to Frappe API
        // For testing, we'll use frappe.call from zvgnext
        const { frappe: frappeClient } = await import("@/lib/frappe-client");
        
        const result = await frappeClient.call<ConsumeTokenResponse>(
          "zaviago_backend.storefront.grida_editor.consume_grida_editor_handoff",
          { token }
        );
        
        return result;
      } catch (error) {
        logger.error("[useGridaTokenConsume] Failed to consume token:", error);
        throw error;
      }
    },
    enabled: !!token,
    retry: false, // Don't retry on token errors
  });
}
